/**
 * Lignes « label / valeur » d'une journée de suivi, par domaine.
 *
 * Extrait de `pages/Calendrier.js`, seul écran qui savait apercevoir les trois domaines.
 * Le calendrier de la page Cycle n'apercevait que le cycle : une journée portant du
 * mental ou du sport, mais aucun relevé de cycle, affichait « Aucune donnée enregistrée »
 * suivie du seul bouton de saisie. Le calendrier des humeurs, lui, n'était pas cliquable
 * du tout.
 */
import { MOOD_EMOJI } from './labels';
import { formatTemperature } from '../utils/temperature';

const joinList = (v) => (Array.isArray(v) && v.length ? v.join(', ') : null);
const yesNo = (v) => (v == null ? null : v ? 'Oui' : 'Non');

/** Construit les lignes d'une section, en éliminant tout ce qui n'a pas été renseigné. */
export function buildDetailRows(sectionId, e) {
  if (!e) return [];
  let rows;
  if (sectionId === 'cycle') {
    rows = [
      ['Flux',               e.flow],
      ['Pertes',             e.discharge],
      ['Température basale', formatTemperature(e)],
      ['Libido',             e.libido],
      ['Fringales',          e.cravings ? (joinList(e.craving_types) ?? 'Oui') : yesNo(e.cravings)],
      ["Test d'ovulation",   yesNo(e.ovulation_test)],
      // Seulement quand elle est vraie : « Jour d'ovulation : Non » sur chacun des
      // 27 autres jours du cycle n'apprendrait rien.
      ["Jour d'ovulation",   e.ovulation_declared ? 'Déclaré par toi' : null],
      ['Symptômes',          joinList(e.physical_symptoms)],
      ['Origine du mal de dos', e.back_pain_type],
      ['Sommeil',            e.sleep_quality],
      ['Réveil',             e.wake_up_state],
    ];
  } else if (sectionId === 'mental') {
    rows = [
      ['Humeur',        e.mood ? `${MOOD_EMOJI[e.mood] ?? '😊'} ${e.mood}` : null],
      ['Concentration', e.concentration != null ? `${e.concentration}/10` : null],
      ['Productivité',  e.productivity  != null ? `${e.productivity}/10`  : null],
      ['Estime de soi', e.self_esteem   != null ? `${e.self_esteem}/10`   : null],
      ['Ressentis',     joinList(e.emotional_symptoms)],
      // Les deux moments s'affichent séparément ; un relevé d'avant le découpage n'a
      // qu'un ressenti global, rendu sur une seule ligne.
      ['Ressenti matin',       e.day_feeling_morning],
      ['Ressenti après-midi',  e.day_feeling_afternoon],
      ['Ressenti de la journée',
        e.day_feeling_morning == null && e.day_feeling_afternoon == null
          ? e.day_feeling
          : null],
    ];
  } else {
    rows = [
      ['Compétition',   e.is_competition ? 'Oui' : null],
      ['Activités',     joinList(e.sports_pratiques)],
      // `sessions` n'est rempli qu'à partir de deux séances dans la journée
      // (cf. schemas/sport.py) : le RPE affiché est alors celui de la plus dure.
      ['Séances',       e.sessions?.length > 1 ? `${e.sessions.length} séances` : null],
      ['RPE',           e.rpe != null ? `${e.rpe}/10` : null],
      ['FC repos',      e.rest_heart_rate   != null ? `${e.rest_heart_rate} bpm`   : null],
      ['FC effort',     e.effort_heart_rate != null ? `${e.effort_heart_rate} bpm` : null],
      ['Essoufflement', e.breathlessness],
      ['Courbatures',   e.soreness_onset && e.soreness_onset !== 'Aucune'
                          ? [e.soreness_onset, e.soreness_level].filter(Boolean).join(' · ')
                          : e.soreness_onset],
      ['Blessure',     e.has_injury ? (e.injury_description || 'Oui') : null],
    ];
  }
  return rows.filter(([, value]) => value != null && value !== '');
}
